import { Map } from "immutable";
import { createSelector } from "reselect";

import {
  CSV_FIELD_LABELS,
  CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT,
  CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT_LABEL,
  CSV_IMPORT_FIELDS
} from "../../constants/importExportFile";
import { propertySelectors } from "../../modules/definition/properties";
import { referenceSelectors } from "../../modules/definition/references";
import { typeSelectors } from "../../modules/definition/types";
import { itemSelectors } from "../../modules/items";
import { tagSelectors } from "../../modules/tags";
import { Precision } from "../../types";
import { createItemLabelMap, createTagValueMap } from "./utils";

const getEmbeddedColumnTypeOptions = createSelector(
  propertySelectors.getOrderedProperties,
  typeSelectors.getOrderedTypes,
  referenceSelectors.getOrderedReferences,
  (propertyList, typesList, referencesList) => {
    const doNotImportOption = {
      label: CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT_LABEL,
      value: CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT
    };
    const normalOptions = CSV_IMPORT_FIELDS.map(f => ({ label: CSV_FIELD_LABELS[f], value: f }));
    const propertyOptions = {
      label: "Properties",
      value: "propertiesGroup",
      children: propertyList.map(p => ({ label: p.label, value: p.id })).toJS()
    };
    const typesOptions = {
      label: "Types",
      value: "typesGroup",
      children: typesList.map(t => ({ label: t.label, value: t.id })).toJS()
    };
    const referencesOptions = {
      label: "Relationships",
      value: "referencesGroup",
      children: referencesList.map(r => ({ label: r.label, value: r.id })).toJS()
    };

    return [doNotImportOption].concat(normalOptions, propertyOptions, typesOptions, referencesOptions);
  }
);

const getColumnTypeOptions = createSelector(
  propertySelectors.getOrderedProperties,
  typeSelectors.getOrderedTypes,
  referenceSelectors.getOrderedReferences,
  (propertyList, typesList, referencesList) => {
    const doNotImportOption = {
      label: CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT_LABEL,
      value: CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT,
      matchLabels: [CSV_IMPORT_COLUMN_TYPE_DO_NOT_IMPORT_LABEL]
    };
    const normalOptions = CSV_IMPORT_FIELDS.map(f => ({
      label: CSV_FIELD_LABELS[f],
      value: f,
      matchLabels: [CSV_FIELD_LABELS[f]]
    }));
    const propertyOptions = propertyList.map(p => ({ label: p.label, value: p.id, matchLabels: [p.label] })).toJS();
    const typesOptions = typesList
      .map(t => ({ label: t.label, value: t.id, matchLabels: [t.label, t.pluralLabel] }))
      .toJS();
    const referencesOptions = referencesList.map(r => ({ label: r.label, value: r.id, matchLabels: [r.label] })).toJS();

    return [doNotImportOption].concat(normalOptions, propertyOptions, typesOptions, referencesOptions);
  }
);

const getItemsByLabel = createSelector(
  // inputs
  itemSelectors.getItemsById,
  typeSelectors.getTypesById,
  // output
  createItemLabelMap
);

const getTagsByTag = createSelector(tagSelectors.getTags, createTagValueMap);

const initColumnInfoOptions = (typesById, referencesById) => {
  // Normal columns
  let optionsMap = Map({
    endDate: false,
    duration: Precision.Year,
    tags: false
  });
  // item types columns
  optionsMap = optionsMap.merge(typesById.map(_t => false));
  // relationships
  optionsMap = optionsMap.merge(referencesById.map(_r => false));
  return optionsMap;
};

export default {
  getColumnTypeOptions,
  getEmbeddedColumnTypeOptions,
  getItemsByLabel,
  getTagsByTag,
  initColumnInfoOptions
};
